import { z } from "zod";
import { modelIdSchema, providerIdSchema, uuidSchema } from "./aiTypes.ts";
import { event, request } from "./defs.ts";
import { relativePathSchema, projectIdSchema } from "./schemas.ts";

/**
 * ChangeSets (milestone M2B): a model may only PROPOSE changes. A ChangeSet is frozen before a human sees it, and
 * approval applies exactly that frozen ChangeSet (identified by its content hash), never "whatever the model wants".
 *
 *   draft ──freeze──► pending_approval ──approve──► applying ──► applied | conflicted | failed
 *     │                      └──reject──► rejected
 *     └──(run cancelled / failed)──► discarded
 *
 * Full file contents live ONLY on the user's machine (agent). The server stores metadata (paths, hashes, line
 * counts, status). Contents transit the server only to render the diff in the browser; they are not persisted.
 */
export const CHANGESET_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "rejected",
  "applying",
  "applied",
  "conflicted",
  "failed",
  "discarded",
] as const;
export const changeSetStatusSchema = z.enum(CHANGESET_STATUSES);
export type ChangeSetStatus = z.infer<typeof changeSetStatusSchema>;

export const CHANGE_OPS = ["create", "modify", "delete", "rename"] as const;
export const changeOpSchema = z.enum(CHANGE_OPS);
export type ChangeOp = z.infer<typeof changeOpSchema>;

export const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const filePath = relativePathSchema.min(1);

export const FILE_RESULTS = ["applied", "conflicted", "failed", "skipped"] as const;
export const fileResultSchema = z.enum(FILE_RESULTS);

/** Content-free description of one file change (what the server stores and the chat shows). */
export const fileChangeSummarySchema = z.strictObject({
  op: changeOpSchema,
  /** Target path (for rename: the new path). */
  path: filePath,
  /** Rename only: the old path. */
  oldPath: filePath.optional(),
  /** SHA-256 of the file the model worked from (null for create). */
  baseHash: sha256Schema.nullable(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
  /** Set after an apply attempt. */
  result: fileResultSchema.optional(),
  code: z.string().max(64).optional(),
});
export type FileChangeSummary = z.infer<typeof fileChangeSummarySchema>;

export const changeSetInfoSchema = z.strictObject({
  id: uuidSchema,
  sessionId: uuidSchema,
  runId: uuidSchema,
  projectId: projectIdSchema,
  provider: providerIdSchema,
  model: modelIdSchema,
  status: changeSetStatusSchema,
  files: z.array(fileChangeSummarySchema).max(100),
  /** Set once frozen; approval must present this exact hash. */
  contentHash: sha256Schema.nullable(),
  message: z.string().max(300).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChangeSetInfo = z.infer<typeof changeSetInfoSchema>;

export const changeSetFileContentSchema = z.strictObject({
  op: changeOpSchema,
  path: filePath,
  oldPath: filePath.optional(),
  /** Text the model worked from (null for create). */
  original: z.string().nullable(),
  /** Proposed text (null for delete). */
  modified: z.string().nullable(),
});
export type ChangeSetFileContent = z.infer<typeof changeSetFileContentSchema>;

const runIds = { runId: uuidSchema, sessionId: uuidSchema, changeSetId: uuidSchema };
const files = z.array(fileChangeSummarySchema).max(100);

/** Agent → server events (the server persists them and re-publishes `changeset.status` to the owner). */
export const changeSetAgentEvents = {
  "agent/changeset.draft": event("agent", "changeset.draft", z.strictObject({ ...runIds, files })),
  "agent/changeset.frozen": event(
    "agent",
    "changeset.frozen",
    z.strictObject({ ...runIds, contentHash: sha256Schema, files }),
  ),
  "agent/changeset.discarded": event(
    "agent",
    "changeset.discarded",
    z.strictObject({ ...runIds, reason: z.string().max(100) }),
  ),
} as const;

/** Server → browser only. Not part of any registry an agent may send. */
export const changeSetBrowserEvents = {
  "agent/changeset.status": event(
    "agent",
    "changeset.status",
    z.strictObject({ changeSet: changeSetInfoSchema }),
  ),
} as const;

export function parseChangeSetStatusEvent(msg: {
  channel: string;
  type: string;
  payload: unknown;
}): ChangeSetInfo | null {
  if (msg.channel !== "agent" || msg.type !== "changeset.status") return null;
  const parsed = changeSetBrowserEvents["agent/changeset.status"].payload.safeParse(msg.payload);
  return parsed.success ? parsed.data.changeSet : null;
}

const changeSetIdOnly = z.strictObject({ changeSetId: uuidSchema });

/** Browser → server (answered by the server; ownership is checked in SQL). */
export const changeSetRequests = {
  "agent/changeset.approve": request(
    "agent",
    "changeset.approve",
    z.strictObject({ changeSetId: uuidSchema, contentHash: sha256Schema }),
    z.strictObject({ changeSet: changeSetInfoSchema }),
  ),
  "agent/changeset.reject": request(
    "agent",
    "changeset.reject",
    changeSetIdOnly,
    z.strictObject({ changeSet: changeSetInfoSchema }),
  ),
  "agent/changeset.content": request(
    "agent",
    "changeset.content",
    changeSetIdOnly,
    z.strictObject({ changeSet: changeSetInfoSchema, files: z.array(changeSetFileContentSchema).max(100) }),
  ),
} as const;

export const APPLY_STATUSES = ["applied", "conflicted", "failed"] as const;

/** Server → agent ONLY (the relay never routes these from browsers). */
export const changeSetCommands = {
  "agent/changeset.apply": request(
    "agent",
    "changeset.apply",
    z.strictObject({ changeSetId: uuidSchema, projectId: projectIdSchema, contentHash: sha256Schema }),
    z.strictObject({
      status: z.enum(APPLY_STATUSES),
      message: z.string().max(300).nullable(),
      files: z
        .array(
          z.strictObject({ path: filePath, result: fileResultSchema, code: z.string().max(64).optional() }),
        )
        .max(100),
    }),
  ),
  "agent/changeset.get": request(
    "agent",
    "changeset.get",
    z.strictObject({ changeSetId: uuidSchema, projectId: projectIdSchema }),
    z.strictObject({ contentHash: sha256Schema, files: z.array(changeSetFileContentSchema).max(100) }),
  ),
  "agent/changeset.discard": request(
    "agent",
    "changeset.discard",
    z.strictObject({ changeSetId: uuidSchema }),
    z.strictObject({ discarded: z.boolean() }),
  ),
} as const;

export type ChangeSetAgentEvent = {
  [K in keyof typeof changeSetAgentEvents]: {
    type: K;
    payload: z.infer<(typeof changeSetAgentEvents)[K]["payload"]>;
  };
}[keyof typeof changeSetAgentEvents];

/** Validated view of an agent → server ChangeSet event, or null for anything else. */
export function parseChangeSetAgentEvent(msg: {
  channel: string;
  type: string;
  payload: unknown;
}): ChangeSetAgentEvent | null {
  const key = `${msg.channel}/${msg.type}`;
  if (!Object.prototype.hasOwnProperty.call(changeSetAgentEvents, key)) return null;
  const def = changeSetAgentEvents[key as keyof typeof changeSetAgentEvents];
  const parsed = def.payload.safeParse(msg.payload);
  return parsed.success ? ({ type: key, payload: parsed.data } as ChangeSetAgentEvent) : null;
}
